// The store owns cart/checkout; the studio owns faces and production files.
// Keep this module importable in Node so cart mutations can be tested offline.
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const PRODUCTS = {
  '45289147662422': { product: 'knob', label: 'Cabinet knob' },
  '45666227322966': { product: 'wine_stopper', label: 'Wine bottle stopper' },
  '50600190181462': { product: 'keychain', label: 'Keychain' }
};
const API = 'https://api-production-3d29.up.railway.app';
const APP = 'https://headknobs-studio-production.up.railway.app';

export function validatePayload(data) {
  if (data?.type !== 'hk:checkout' || data.version !== 1 || !UUID.test(data.batch) ||
      !Array.isArray(data.items) || !data.items.length || data.items.length > 100) {
    throw new Error('Your order could not be read. Return to the builder and try again.');
  }
  const ids = new Set();
  const items = data.items.map(item => {
    const id = String(item.id);
    const p = item.properties || {};
    if (!PRODUCTS[id] || !Number.isInteger(item.quantity) || item.quantity < 1 || item.quantity > 50 ||
        !UUID.test(p._pipeline_order_id) || !UUID.test(p._source_model_id) ||
        p._headknobs_builder !== data.batch || ids.has(p._pipeline_order_id)) {
      throw new Error('An object in your order is invalid. Please return to the builder.');
    }
    ids.add(p._pipeline_order_id);
    return { id, quantity: item.quantity, properties: {
      Face: String(p.Face || 'Custom face').slice(0, 60), Product: PRODUCTS[id].label, Material: 'Satin nickel',
      _pipeline_order_id: p._pipeline_order_id, _source_model_id: p._source_model_id, _headknobs_builder: data.batch
    } };
  });
  return { batch: data.batch, items };
}

// What a synced cart holds, so a later checkout can tell whether the cart it
// prepared still matches the basket.
export function cartKey(data) {
  const { batch, items } = validatePayload(data);
  return batch + '|' + items.map(i => [i.id, i.quantity, i.properties._pipeline_order_id, i.properties._source_model_id].join(':')).sort().join(',');
}
// A prepared cart is trusted this long; after that checkout re-syncs it.
const PREPARED_FOR_MS = 20 * 60 * 1000;

export async function syncBuilderCart(data, { request, manifest }) {
  const { batch, items } = validatePayload(data);
  // Validate all production records before making any cart change.
  await Promise.all(items.map(async item => {
    const m = await manifest(item.properties._pipeline_order_id);
    if (m.order_id !== item.properties._pipeline_order_id || m.batch !== batch ||
        m.source_order_id !== item.properties._source_model_id || m.product !== PRODUCTS[item.id].product) {
      throw new Error('An object no longer matches its model. Please return to the builder.');
    }
  }));
  let cart = await request('cart.js');
  if (cart.currency !== 'USD') throw new Error('Please select USD before continuing to checkout.');
  const desired = new Map(items.map(i => [i.properties._pipeline_order_id, i]));
  const matched = new Set();
  const updates = {};
  for (const line of cart.items) {
    const lineBatch = line.properties?._headknobs_builder;
    if (!lineBatch) continue;
    // A builder line from another basket was either adopted into this one
    // (and is re-added under this batch) or removed by the customer.
    if (lineBatch !== batch) { updates[line.key] = 0; continue; }
    const pid = line.properties._pipeline_order_id;
    const wanted = desired.get(pid);
    const keep = wanted && !matched.has(pid) && String(line.variant_id) === wanted.id &&
      line.properties._source_model_id === wanted.properties._source_model_id;
    updates[line.key] = keep ? wanted.quantity : 0;
    if (keep) matched.add(pid);
  }
  // Keys (not variant IDs) preserve different faces of the same product and
  // unrelated cart contents. Set absolute quantities so retrying is safe.
  if (Object.keys(updates).length) await request('cart/update.js', { updates });
  const missing = items.filter(i => !matched.has(i.properties._pipeline_order_id));
  if (missing.length) await request('cart/add.js', { items: missing });
  cart = await request('cart.js');
  const own = cart.items.filter(i => i.properties?._headknobs_builder === batch);
  const stale = cart.items.some(i => i.properties?._headknobs_builder && i.properties._headknobs_builder !== batch);
  if (stale || own.length !== items.length || items.some(wanted => {
    const actual = own.find(i => i.properties._pipeline_order_id === wanted.properties._pipeline_order_id);
    return !actual || String(actual.variant_id) !== wanted.id || actual.quantity !== wanted.quantity ||
      actual.properties._source_model_id !== wanted.properties._source_model_id;
  })) throw new Error('Your cart changed while we were updating it. Please try again.');
  // Prices and discounts are Shopify's job; its checkout shows the real total.
  return cart;
}

// A return from checkout must restore Shopify's actual quantities, not a
// possibly stale browser basket. Older/mixed carts retain their normal cart.
export function cartReturnPayload(cart) {
  if (!Array.isArray(cart?.items) || !cart.items.length || cart.currency !== 'USD') return null;
  const batch = cart.items[0].properties?._headknobs_builder;
  const data = { type: 'hk:checkout', version: 1, batch, items: cart.items.map(line => ({ id: String(line.variant_id), quantity: line.quantity, properties: line.properties })) };
  try {
    const valid = validatePayload(data);
    const pairs = valid.items.map(item => `${item.properties._source_model_id}:${item.id}`);
    if (new Set(pairs).size !== pairs.length) return null;
    return { type: 'hk:restore-cart', version: 1, ...valid };
  } catch { return null; }
}

// Builder lines in the cart, for the studio to offer back any that belong to
// a basket it forgot (a closed tab can lose its basket, not the cart).
export function builderCartLines(cart) {
  if (!Array.isArray(cart?.items) || cart.currency !== 'USD') return [];
  return cart.items.filter(line => {
    const p = line.properties || {};
    return PRODUCTS[String(line.variant_id)] && UUID.test(p._headknobs_builder) && UUID.test(p._source_model_id) &&
      UUID.test(p._pipeline_order_id) && Number.isInteger(line.quantity) && line.quantity >= 1 && line.quantity <= 50;
  }).slice(0, 100).map(line => ({
    id: String(line.variant_id), quantity: line.quantity, batch: line.properties._headknobs_builder,
    face: String(line.properties.Face || 'Saved face').slice(0, 60),
    source: line.properties._source_model_id, line: line.properties._pipeline_order_id
  }));
}

export function headerScrollOffset(header, target) {
  if (!header) return 0;
  const style = getComputedStyle(header);
  // A sticky header inside its own short Shopify section scrolls away.
  // Reserve space only for a fixed header or one that can stick over target.
  if (style.position === 'fixed' || (style.position === 'sticky' && header.parentElement?.contains(target))) {
    return header.offsetHeight + (parseFloat(style.top) || 0);
  }
  return 0;
}

let busy = false;
if (typeof window !== 'undefined' && !customElements.get('hk-builder-embed')) {
  customElements.define('hk-builder-embed', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.style.overflowAnchor = 'none';
      this.frame = this.querySelector('iframe');
      this.message = this.querySelector('[data-builder-status]');
      this.retry = this.querySelector('[data-builder-retry]');
      this.cartLink = this.querySelector('[data-builder-cart]');
      this.retry?.addEventListener('click', () => this.checkout(this.pending));
      if (!this.frame) return;
      this.onMessage = event => {
        if (event.source !== this.frame.contentWindow || event.origin !== APP || event.data?.version !== 1) return;
        const data = event.data;
        if (data.type === 'hk:library-state' && new URLSearchParams(location.search).get('review') === '1' && !this.restoringCart) {
          this.restoringCart = true;
          this.frame.contentWindow.postMessage({ type: 'hk:cart-loading', version: 1 }, APP);
          const root = window.Shopify?.routes?.root || '/';
          fetch(root + 'cart.js', { credentials: 'same-origin', cache: 'no-store' })
            .then(response => { if (!response.ok) throw new Error('Cart unavailable'); return response.json(); })
            .then(cart => {
              const payload = cartReturnPayload(cart);
              if (!payload) { location.replace(root + 'cart?builder_return=0'); return; }
              this.frame.contentWindow.postMessage(payload, APP);
            }).catch(() => location.replace(root + 'cart?builder_return=0'));
        }
        if (data.type === 'hk:library-state' && new URLSearchParams(location.search).get('review') !== '1' && !this.offeredCart) {
          this.offeredCart = true;
          const root = window.Shopify?.routes?.root || '/';
          fetch(root + 'cart.js', { credentials: 'same-origin', cache: 'no-store' })
            .then(response => response.ok ? response.json() : null)
            .then(cart => {
              const items = builderCartLines(cart);
              if (items.length) this.frame.contentWindow.postMessage({ type: 'hk:cart-lines', version: 1, items }, APP);
            }).catch(() => {}); // Checkout still clears what it can't show.
        }
        // Tell the studio this page can prepare a cart quietly (an older page
        // would treat a prepare request as a full checkout and leave).
        if (data.type === 'hk:library-state') this.frame.contentWindow.postMessage({ type: 'hk:bridge', version: 1, features: ['prepare'] }, APP);
        // Hand the studio Shopify's current prices so a sitewide sale shows there too.
        if (data.type === 'hk:library-state' && this.dataset.prices) {
          try { this.frame.contentWindow.postMessage({ type: 'hk:prices', version: 1, prices: JSON.parse(this.dataset.prices) }, APP); } catch { /* keep the studio's list prices */ }
        }
        if (data.type === 'hk:preview' && typeof data.open === 'boolean') this.setPreview(data.open);
        if (data.type === 'hk:library-state' && new URLSearchParams(location.search).get('library') === '1' && !this.openedLibrary) {
          this.openedLibrary = true;
          this.frame.contentWindow.postMessage({ type: 'hk:open-library', version: 1 }, APP);
        }
        if (data.type === 'hk:resize' && Number.isFinite(data.height) && !this.previewRestore) {
          this.frame.style.height = `${Math.max(600, Math.min(30000, Math.ceil(data.height) + 8))}px`;
          this.onViewport();
        }
        if (data.type === 'hk:scroll' && Number.isFinite(data.top)) {
          const header = document.querySelector('.hk-header');
          const top = window.scrollY + this.frame.getBoundingClientRect().top +
            Math.max(0, Math.min(data.top, this.frame.offsetHeight)) - headerScrollOffset(header, this);
          window.scrollTo({ top, behavior: data.instant || matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
        }
        if (data.type === 'hk:checkout') this.checkout(data);
      };
      window.addEventListener('message', this.onMessage);
      // The frame is as tall as its content, so this page does the scrolling.
      // Tell the studio which frame offset sits just below the header (its
      // face rail follows the reader down from that) and how tall the visible
      // area is.
      this.onViewport = () => {
        if (this.viewportQueued) return;
        this.viewportQueued = true;
        requestAnimationFrame(() => {
          this.viewportQueued = false;
          const below = headerScrollOffset(document.querySelector('.hk-header'), this);
          const top = below - this.frame.getBoundingClientRect().top;
          this.frame.contentWindow?.postMessage({ type: 'hk:viewport', version: 1, top, height: window.innerHeight - below }, APP);
        });
      };
      window.addEventListener('scroll', this.onViewport, { passive: true });
      window.addEventListener('resize', this.onViewport);
      this.frame.addEventListener('load', this.onViewport);
      // A standalone return link must not mutate a cart just by being opened.
      // Ask for a click; hash data is never sent to our web server.
      if (location.hash.startsWith('#hk-checkout=')) {
        try {
          const encoded = decodeURIComponent(location.hash.slice(13));
          if (encoded.length > 100000) throw new Error('Order link is too large.');
          const bytes = Uint8Array.from(atob(encoded), c => c.charCodeAt(0));
          const data = JSON.parse(new TextDecoder().decode(bytes));
          validatePayload(data);
          this.pending = data;
          this.show('Your objects are ready.', true);
          this.retry.textContent = 'Continue to checkout';
          history.replaceState(null, '', location.pathname + location.search);
        } catch { this.show('This order link could not be read. Please return to the builder.'); }
      }
    }
    disconnectedCallback() {
      this.setPreview(false);
      window.removeEventListener('message', this.onMessage);
      window.removeEventListener('scroll', this.onViewport);
      window.removeEventListener('resize', this.onViewport);
      this.frame?.removeEventListener('load', this.onViewport);
      this.initialized = false;
    }
    setPreview(open) {
      if (open && !this.previewRestore) {
        this.previewRestore = { top: window.scrollY, body: document.body.style.cssText, rootOverflow: document.documentElement.style.overflow, embed: this.style.cssText, frame: this.frame.style.cssText };
        Object.assign(document.body.style, { position: 'fixed', top: `-${window.scrollY}px`, width: '100%', overflow: 'hidden' });
        document.documentElement.style.overflow = 'hidden';
        Object.assign(this.style, { position: 'fixed', inset: '0', zIndex: '2147483000', background: '#fffdf2' });
        Object.assign(this.frame.style, { width: '100%', height: '100dvh' });
      } else if (!open && this.previewRestore) {
        const saved = this.previewRestore;
        this.previewRestore = null;
        document.body.style.cssText = saved.body;
        document.documentElement.style.overflow = saved.rootOverflow;
        this.style.cssText = saved.embed;
        this.frame.style.cssText = saved.frame;
        window.scrollTo({ top: saved.top, behavior: 'instant' });
      }
    }
    show(message, retry = false) {
      this.message.textContent = message;
      this.message.parentElement.hidden = false;
      this.retry.hidden = !retry;
      this.cartLink.hidden = !retry;
    }
    // `prepare` fills the cart quietly while the customer is on the builder's
    // checkout screen (or in the survey): no status bar, no navigation.
    // Checking out a basket prepared moments ago skips straight to checkout.
    async checkout(data) {
      const quiet = data?.prepare === true;
      // A real checkout arriving mid-prepare runs right after it (usually as
      // the fast path); a second prepare is dropped.
      if (busy) { if (!quiet) this.queued = data; return; }
      busy = true;
      if (!quiet) {
        this.pending = data;
        this.show('Checking your objects and cart…');
      }
      const request = async (path, body) => {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(root + path, {
          method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          ...(body ? { body: JSON.stringify(body) } : {}), signal: AbortSignal.timeout(15000)
        });
        const result = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(result.description || 'We couldn’t update your cart. Please try again.');
        return result;
      };
      try {
        if (window.Shopify?.designMode) throw new Error('Checkout is disabled inside the theme editor. Open the theme preview to test.');
        const key = cartKey(data);
        const prepared = !quiet && this.prepared?.key === key && Date.now() - this.prepared.at < PREPARED_FOR_MS;
        const run = () => syncBuilderCart(data, { request, manifest: async id => {
          const response = await fetch(`${API}/api/orders/${id}/checkout-manifest`, { signal: AbortSignal.timeout(15000) });
          const result = await response.json().catch(() => ({}));
          if (!response.ok) throw new Error(result.detail || 'Could not check your production file. Please try again.');
          return result;
        } });
        if (!prepared) {
          if (navigator.locks) await navigator.locks.request('headknobs-cart', { ifAvailable: true }, lock => {
            if (!lock) throw new Error('Your cart is being updated in another tab. Please try again.');
            return run();
          });
          else await run();
          this.prepared = { key, at: Date.now() };
        }
        this.frame.contentWindow.postMessage({ type: 'hk:checkout-result', version: 1, batch: data.batch, ...(quiet ? { prepared: true } : {}) }, APP);
        if (!quiet) location.assign((window.Shopify?.routes?.root || '/') + 'checkout');
      } catch (error) {
        this.prepared = null;
        if (!quiet) {
          this.show(error.message, true);
          this.retry.textContent = 'Try again';
        }
        this.frame.contentWindow.postMessage({ type: 'hk:checkout-result', version: 1, batch: data?.batch, error: error.message }, APP);
      } finally {
        busy = false;
        const next = this.queued;
        this.queued = null;
        if (next) this.checkout(next);
      }
    }
  });
}